import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { useDispatch } from 'react-redux';
import { useGenerateDeviceCodeMutation, useCheckDeviceCodeMutation } from 'store/deviceVerification/deviceVerification.api';
import { activateTokenAfterOTP } from 'store/authentication/authentication.slice';
import { getFingerPrint } from 'utils/webID';
import WebWorker from 'utils/web-worker';
import authWorker from 'utils/auth-worker';
import { CustomButton } from 'components/CustomButton/CustomButton';
import { LocalStorage } from 'services';
import {
  STitle,
  SSubTitle,
  SSmsCodeForm,
  SDescription,
  SResendCode,
} from './DeviceVerifyPage.styles';

const DeviceVerifyPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [code, setCode] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  
  const worker = new WebWorker(authWorker);
  
  const [generateDeviceCode] = useGenerateDeviceCodeMutation();
  const [checkDeviceCode] = useCheckDeviceCodeMutation();

  const generateCode = useCallback(async () => {
    if (!transactionId) return;
    
    try {
      const fingerprint = getFingerPrint(true) as string;
      
      await generateDeviceCode({
        transactionId,
        fingerprint,
        codeDelivery: 'Email',
      }).unwrap();
       
      message.success('Verification code sent to your email');
    } catch (err: any) {
      setErrorMsg(err?.data?.Error || 'Failed to send code');
    }
  }, [transactionId, generateDeviceCode]);

  const handleSubmitCode = async () => {
    if (!transactionId || !code) return;

    try {
      setLoading(true);
      setErrorMsg('');
      
      const response = await checkDeviceCode({
        transactionId,
        code,
      }).unwrap();
      
      if (response.deviceId) {
        LocalStorage.set('DEVICE_ID', response.deviceId);
      }
      
      const finalToken = response.token;
      
      if (finalToken) {
        dispatch(activateTokenAfterOTP({ 
          token: finalToken,
          username: response.client?.username || '',
          checksum: response.otpSeed
        }));
        
        worker.postMessage({ action: 'SET', token: finalToken });
      } else {
        throw new Error('No token received from verification');
      }
      
      LocalStorage.remove('LOGIN_OTP_FLOW');
      
      if (response.client) {
        LocalStorage.set('clientData', response.client, true);
      }
      
      message.success('Device verified successfully!');
      
      const redirectUrl = response.systemProperties?.postLoginURL || '/';
      navigate(redirectUrl);
      
    } catch (err: any) {
      setErrorMsg(err?.data?.Error || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleCompletion = (codeVal: string) => {
    setCode(codeVal);
  };

  const handleResendCode = useCallback(() => {
    generateCode();
  }, [generateCode]);

  useEffect(() => {
    try {
      const otpFlowData = LocalStorage.get('LOGIN_OTP_FLOW');
      
      if (otpFlowData?.transactionId) {
        setTransactionId(otpFlowData.transactionId);
      } else {
        message.error('No verification session found. Please start again.');
        navigate('/verify-device-warning', { replace: true });
      }
    } catch (error) {
      message.error('Failed to retrieve verification session. Please start again.');
      navigate('/verify-device-warning', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (transactionId) {
      generateCode();
    }
  }, [transactionId, generateCode]);

  return (
    <>
      <STitle>Device Verification</STitle>
      <SSubTitle>Enter the verification code</SSubTitle>

      <SSmsCodeForm 
        handleCompletion={handleCompletion} 
        onCompletion={setIsSubmitDisabled} 
        checkError={errorMsg || undefined}
        size={5}
      />

      <CustomButton 
        type="primary" 
        block 
        buttonSize="large" 
        htmlType="submit" 
        onClick={handleSubmitCode} 
        disabled={isSubmitDisabled || loading} 
        marginBottom={52}
      >
        {isSubmitDisabled ? 'Continue' : 'Verify'}
      </CustomButton>

      <SDescription>Didn&apos;t receive verification code?</SDescription>
      <SResendCode onClick={handleResendCode}>Resend Code</SResendCode>
    </>
  );
};

export default DeviceVerifyPage; 